import {
  createBigDecimal,
  greaterThan,
  minus,
  plus,
} from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { AssetBalance } from '../../../types/assetBalance'
import type { AssetBorrow } from '../../../types/assetBorrow'
import type { CostBasis } from '../../../types/costBasis'
import type { Transaction } from '../../../types/transaction'
import { createAssetKey } from '../../../utils'
import { createInsufficientTransaction } from '../createInsufficientTransaction'

type CreateInsufficientTransactionByTransferParameters = {
  readonly actionId: string
  readonly timestamp: number
  readonly transfer: Transfer
  readonly assetBalances: Map<string, readonly AssetBalance[]>
  readonly assetBorrows: Map<string, readonly AssetBorrow[]>
  readonly usedBalances: Map<string, AssetBalance>
  readonly costBasis: CostBasis
  readonly onlyUseBorrows?: boolean
}

type CreateInsufficientTransactionByTransferReturnTypes =
  | {
      readonly transaction: Transaction
      readonly assetBalances: Map<string, readonly AssetBalance[]>
      readonly assetBorrows: Map<string, readonly AssetBorrow[]>
      readonly usedBalances: Map<string, AssetBalance>
    }
  | undefined

export const createInsufficientTransactionByTransfer = ({
  actionId,
  timestamp,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
  onlyUseBorrows = false,
}: CreateInsufficientTransactionByTransferParameters): CreateInsufficientTransactionByTransferReturnTypes => {
  if (transfer.asset.type === 'FiatCurrency') {
    return undefined
  }

  const key = createAssetKey(transfer.asset)
  const totalBalance = onlyUseBorrows
    ? createBigDecimal(0n)
    : (assetBalances.get(key) ?? []).reduce(
        (acc, { amount }) => plus(acc, amount),
        createBigDecimal(0n),
      )
  if (!onlyUseBorrows && !greaterThan(transfer.amount, totalBalance)) {
    return undefined
  }
  const totalBorrow = (assetBorrows.get(key) ?? []).reduce(
    (acc, { amount }) => plus(acc, amount),
    createBigDecimal(0n),
  )
  const insufficientAmount = minus(
    transfer.amount,
    plus(totalBalance, totalBorrow),
  )
  if (insufficientAmount.value <= 0n) {
    return undefined
  }

  return createInsufficientTransaction({
    actionId,
    timestamp,
    transfer: { ...transfer, amount: insufficientAmount },
    assetBalances,
    assetBorrows,
    usedBalances,
    costBasis,
  })
}
